/**
 * WelcomePopupWidget · Choice-Verteilung des Welcome-Choice-Popups
 * ================================================================
 *
 * Rendert eine kompakte Statistik-Karte mit:
 *   - Range-Filter oben rechts (via bestehendem AnalyticsRangeFilter)
 *   - 5 horizontalen Balken (shown / referenz / rabatt / informieren / closed)
 *   - großer Conversion-Rate-Zeile unten (CTA-Klicks / shown)
 *
 * Zwei Einsatzorte:
 *   1) `/admin/analytics` (Tagesbericht) — mit externem Range-Filter
 *      (`externalRange` + `onExternalRangeChange` setzen)
 *   2) `/admin/aktions-seite` (Popup-Editor) — mit eigenem, internem
 *      Range-Filter (Props leer lassen, Widget verwaltet den State selbst)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import AnalyticsRangeFilter from "@/components/AnalyticsRangeFilter";
import { api } from "@/lib/auth";
import { Gift, Percent, ArrowRight, X as XIcon, Eye, RefreshCw, Check } from "lucide-react";

type Choice = "shown" | "referenz" | "rabatt" | "informieren" | "closed"
    | "submitted_referenz" | "submitted_rabatt";

type WelcomeChoicePayload = {
    range: string;
    counts: Record<Choice, number>;
    totals: {
        clicked_cta: number;
        dismissed: number;
        submits: number;
        click_to_submit_rate: number;
        shown_to_submit_rate: number;
        conversion_rate: number;
    };
};

type Props = {
    /** Titel der Karte (kontextabhängig).
     *  Default: „Willkommens-Popup · Live-Statistik". */
    title?: string;
    /**
     * Wenn gesetzt, verwendet das Widget diesen externen Range-Wert und
     * meldet Änderungen via `onExternalRangeChange` zurück — sinnvoll für
     * Seiten, die einen gemeinsamen Range-State haben (z. B.
     * `/admin/analytics`). Andernfalls verwaltet das Widget seinen Range
     * selbst (Default `7d`).
     */
    externalRange?: string;
    onExternalRangeChange?: (next: string) => void;
    /**
     * Wenn `true`, wird der Range-Filter im Widget-Header ausgeblendet
     * (nur externer Filter der Seite ist relevant, Widget rendert die
     * gerade globale Auswahl). Default `false`.
     */
    hideInternalFilter?: boolean;
    /** Optionale zusätzliche CSS-Klassen für den Karten-Wrapper. */
    className?: string;
};

/** Balken-Metadaten in fester Anzeige-Reihenfolge.
 *
 *  Layout des Trichters:
 *      1) Interaktions-Ebene:  shown → referenz-Klick, rabatt-Klick,
 *                              informieren, closed
 *      2) [Divider — Submit-Ebene]
 *      3) Submit-Ebene:        submitted_referenz, submitted_rabatt
 *
 *  Farben spiegeln die Popup-CTAs 1:1 wider (Gold/Rot). Submits nutzen
 *  eine dunklere Nuance derselben Farbe → visueller Zusammenhang „Klick →
 *  Submit" ohne dass die 4 Farbwelten kollidieren. `section` steuert die
 *  Renderer-Gruppierung (Klick-Balken vs. Submit-Balken).
 */
const BAR_META: Array<{
    key: Choice;
    label: string;
    color: string;
    icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
    section: "click" | "submit";
}> = [
    { key: "shown",              label: "Angezeigt",             color: "#94A3B8", icon: Eye,        section: "click" },
    { key: "referenz",           label: "100 % Referenz · Klick", color: "#A78150", icon: Gift,       section: "click" },
    { key: "rabatt",             label: "80 % Rabatt · Klick",    color: "#C53030", icon: Percent,    section: "click" },
    { key: "informieren",        label: "Zuerst informieren",    color: "#64748B", icon: ArrowRight, section: "click" },
    { key: "closed",             label: "Geschlossen",           color: "#475569", icon: XIcon,      section: "click" },
    { key: "submitted_referenz", label: "Abgeschickt Referenz",  color: "#6E5432", icon: Check,      section: "submit" },
    { key: "submitted_rabatt",   label: "Abgeschickt Rabatt",    color: "#7F1D1D", icon: Check,      section: "submit" },
];

export default function WelcomePopupWidget({
    title = "Willkommens-Popup · Live-Statistik",
    externalRange,
    onExternalRangeChange,
    hideInternalFilter = false,
    className,
}: Props) {
    /** Interner Fallback-Range wenn kein externer gesetzt ist. */
    const [internalRange, setInternalRange] = useState<string>("7d");
    const range = externalRange ?? internalRange;
    const setRange = useCallback((next: string) => {
        if (onExternalRangeChange) onExternalRangeChange(next);
        else setInternalRange(next);
    }, [onExternalRangeChange]);

    const [data, setData] = useState<WelcomeChoicePayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        const res = await api<WelcomeChoicePayload>(
            `/api/admin/analytics/welcome-choice?range=${encodeURIComponent(range)}`,
        );
        if (res.ok && res.data) setData(res.data);
        else setError(res.error || "Daten konnten nicht geladen werden.");
        setLoading(false);
    }, [range]);

    useEffect(() => { void load(); }, [load]);

    /** Skalierungsbasis für die Balken: `max(counts)`. Fallback auf 1,
     *  damit auch bei leerem Zeitraum keine Division-durch-null passiert. */
    const maxCount = useMemo(() => {
        if (!data) return 1;
        const vals = Object.values(data.counts);
        return Math.max(1, ...vals);
    }, [data]);

    const isEmpty = !!data && Object.values(data.counts).every((v) => v === 0);

    return (
        <div
            className={`rounded-sm ${className || ""}`}
            style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            data-testid="welcome-popup-widget"
        >
            {/* Header */}
            <div
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b"
                style={{ borderColor: "hsl(var(--hf-line))" }}
            >
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Widget</div>
                    <div className="text-sm font-semibold text-hf-ink" data-testid="welcome-popup-widget-title">
                        {title}
                    </div>
                </div>
                {!hideInternalFilter && (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => void load()}
                            className="hf-btn hf-btn-ghost"
                            aria-label="Aktualisieren"
                            data-testid="welcome-popup-widget-refresh"
                        >
                            <RefreshCw size={13} />
                        </button>
                        <AnalyticsRangeFilter
                            value={range}
                            onChange={setRange}
                            testIdPrefix="welcome-popup-widget-range"
                        />
                    </div>
                )}
            </div>

            {/* Body */}
            <div className="p-4">
                {loading && (
                    <div className="py-6 text-center text-sm text-hf-muted" data-testid="welcome-popup-widget-loading">
                        Lädt…
                    </div>
                )}
                {!loading && error && (
                    <div
                        className="py-3 px-3 text-sm rounded-sm"
                        style={{
                            background: "rgba(239, 68, 68, 0.08)",
                            color: "#B91C1C",
                            border: "1px solid rgba(239, 68, 68, 0.3)",
                        }}
                        data-testid="welcome-popup-widget-error"
                    >
                        {error}
                    </div>
                )}
                {!loading && !error && data && (
                    <>
                        {isEmpty ? (
                            <div className="py-8 text-center text-sm text-hf-muted italic" data-testid="welcome-popup-widget-empty">
                                Noch keine Daten in diesem Zeitraum.
                            </div>
                        ) : (
                            <div className="grid gap-2" data-testid="welcome-popup-widget-bars">
                                {BAR_META.map((meta, idx) => {
                                    const value = data.counts[meta.key];
                                    const pct = (value / maxCount) * 100;
                                    const Icon = meta.icon;
                                    const prev = BAR_META[idx - 1];
                                    // Divider zwischen Interaktions- und Submit-Ebene:
                                    // sobald der Section-Wechsel eintritt, kleiner
                                    // Trennstrich mit Label „Submits".
                                    const showDivider = prev && prev.section !== meta.section;
                                    return (
                                        <div key={meta.key}>
                                            {showDivider && (
                                                <div
                                                    className="flex items-center gap-2 my-2 text-[9px] uppercase tracking-[0.24em] text-hf-muted"
                                                    data-testid="welcome-popup-widget-divider"
                                                >
                                                    <span className="flex-1 h-px" style={{ background: "hsl(var(--hf-line))" }} />
                                                    <span>Submits</span>
                                                    <span className="flex-1 h-px" style={{ background: "hsl(var(--hf-line))" }} />
                                                </div>
                                            )}
                                            <div
                                                className="grid items-center gap-2"
                                                style={{ gridTemplateColumns: "200px 1fr 40px" }}
                                                data-testid={`welcome-popup-widget-row-${meta.key}`}
                                            >
                                                <div className="flex items-center gap-1.5 text-xs text-hf-ink">
                                                    <Icon size={13} strokeWidth={2.2} className="text-hf-muted" />
                                                    <span>{meta.label}</span>
                                                </div>
                                                <div
                                                    className="h-5 rounded-sm relative overflow-hidden"
                                                    style={{ background: "hsl(var(--hf-surface) / 0.6)" }}
                                                >
                                                    <div
                                                        className="h-full rounded-sm transition-[width]"
                                                        style={{
                                                            width: `${Math.max(2, pct)}%`,
                                                            background: meta.color,
                                                            transitionDuration: "400ms",
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    className="text-xs font-mono text-right text-hf-ink"
                                                    data-testid={`welcome-popup-widget-count-${meta.key}`}
                                                >
                                                    {value}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Konversionsraten · zwei Kennzahlen prominent
                         *   1) Klick → Submit  (aus Popup-Klicks)
                         *   2) Angezeigt → Submit  (Full-Funnel) */}
                        {!isEmpty && (
                            <div
                                className="mt-4 pt-4 border-t grid gap-3 md:grid-cols-2"
                                style={{ borderColor: "hsl(var(--hf-line))" }}
                                data-testid="welcome-popup-widget-conversion"
                            >
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <span className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">
                                        Klick → Submit
                                    </span>
                                    <span
                                        className="text-xl font-semibold"
                                        style={{ color: "hsl(var(--hf-ink))" }}
                                        data-testid="welcome-popup-widget-click-to-submit"
                                    >
                                        {(data.totals.click_to_submit_rate * 100).toFixed(1).replace(".", ",")} %
                                    </span>
                                    <span className="text-xs text-hf-muted">
                                        ({data.totals.submits} von {data.totals.clicked_cta} Klicks)
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <span className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">
                                        Angezeigt → Submit
                                    </span>
                                    <span
                                        className="text-xl font-semibold"
                                        style={{ color: "hsl(var(--hf-ink))" }}
                                        data-testid="welcome-popup-widget-shown-to-submit"
                                    >
                                        {(data.totals.shown_to_submit_rate * 100).toFixed(1).replace(".", ",")} %
                                    </span>
                                    <span className="text-xs text-hf-muted">
                                        ({data.totals.submits} von {data.counts.shown} gesehen · {data.totals.dismissed} dismissed)
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
